/**
 * GameConfigJSON — Unified config format that bridges the editor to the slot-core game engine.
 *
 * This is a flat, JSON-serializable schema written to `slot-core/game.config.json`.
 * The slot-core runtime reads this file at startup to configure the game.
 */

// ============================================================================
// Main Config Interface
// ============================================================================

export interface GameConfigJSON {
  /** Schema version — bump when the shape changes */
  version: 1;

  game: {
    id: string;
    name: string;
    canvasWidth: number;
    canvasHeight: number;
  };

  grid: {
    rows: number;
    columns: number;
    /** Per-column row counts for non-rectangular grids (e.g. [4,5,6,5,4] diamond) */
    reelHeights?: number[];
    /** 'count' = scatter-pays (Gates of Olympus), 'ways' = ways-pay (Mahjong Ways) */
    winType: 'count' | 'ways';
    symbolWidth: number;
    symbolHeight: number;
    symbolSpacing: number;
  };

  symbols: GameConfigSymbol[];

  cascade: {
    enabled: boolean;
    removeDuration: number;
    fillDuration: number;
    gravityDuration: number;
    columnDelay: number;
    fallStyle: 'bounce' | 'smooth' | 'elastic' | 'drop' | 'fade';
    winHighlightScale: number;
  };

  multiplierPool: Array<{ value: number; weight: number }>;

  freeSpins: {
    triggerSymbolId: string;
    triggerCount: number;
    spinsAwarded: number;
    retriggerCount: number;
    retriggerSpinsAwarded: number;
  };

  economy: {
    betLevels: number[];
    startingBalance: number;
    buyFeatureEnabled: boolean;
    buyFeatureCostMultiplier: number;
    maxWinMultiplier: number;
  };

  spinAnimation: {
    spinDuration: number;
    turboMultiplier: number;
    winPresentDuration: number;
  };

  /** Keyed by part name (e.g. 'header', 'grid', 'spin_button') */
  uiLayout: Record<string, GameConfigUIPartLayout>;

  background: {
    color: string;
    imagePath?: string;
    imageFit?: 'cover' | 'contain' | 'fill';
  };

  /** Optional live API integration config */
  api?: {
    enabled: boolean;
    gameId: string;
    integrationUrl: string;
  };
}

// ============================================================================
// Sub-types
// ============================================================================

export interface GameConfigSymbol {
  id: string;
  name: string;
  category: 'high' | 'normal' | 'scatter' | 'bonus' | 'wild';
  color: string;
  textColor: string;
  weight: number;
  freeGameWeight: number;
  paytable: Array<{ minCount: number; maxCount: number; payout: number }>;
  displayMode: 'color' | 'sprite' | 'spine';
  textureAssetPath?: string;
  spineConfig?: {
    skeletonPath: string;
    atlasPath: string;
    texturePaths: string[];
    animations: Record<string, string>;  // trigger → animation name
    defaultSkin?: string;
    timeScale: number;
  };
}

export interface GameConfigUIPartLayout {
  visible: boolean;
  offsetX: number;
  offsetY: number;
  customWidth: number | null;
  customHeight: number | null;
  anchorX?: number;
  anchorY?: number;
  // Text styling
  fontSize?: number;
  fontColor?: string;
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold';
  textContent?: string;
  textAlign?: 'left' | 'center' | 'right';
  // Button / panel styling
  bgColor?: string;
  textColor?: string;
  borderRadius?: number;
  // Texture
  textureAssetPath?: string | null;
  textureFit?: 'cover' | 'contain' | 'fill';
  // Grid-specific
  symbolScale?: number;
  gridPadding?: number;
}

// ============================================================================
// Default Config
// ============================================================================

export const DEFAULT_GAME_CONFIG: GameConfigJSON = {
  version: 1,

  game: {
    id: 'untitled',
    name: 'Untitled Slot Game',
    canvasWidth: 720,
    canvasHeight: 1280,
  },

  grid: {
    rows: 6,
    columns: 5,
    winType: 'count',
    symbolWidth: 120,
    symbolHeight: 100,
    symbolSpacing: 4,
  },

  symbols: [
    { id: 'H1', name: 'Crown', category: 'high', color: '#DC2626', textColor: '#FFFFFF', weight: 40, freeGameWeight: 95, paytable: [{ minCount: 8, maxCount: 9, payout: 200 }, { minCount: 10, maxCount: 11, payout: 500 }, { minCount: 12, maxCount: 30, payout: 1000 }], displayMode: 'color' },
    { id: 'H2', name: 'Hourglass', category: 'high', color: '#7C3AED', textColor: '#FFFFFF', weight: 40, freeGameWeight: 100, paytable: [{ minCount: 8, maxCount: 9, payout: 50 }, { minCount: 10, maxCount: 11, payout: 200 }, { minCount: 12, maxCount: 30, payout: 500 }], displayMode: 'color' },
    { id: 'H3', name: 'Ring', category: 'high', color: '#2563EB', textColor: '#FFFFFF', weight: 40, freeGameWeight: 100, paytable: [{ minCount: 8, maxCount: 9, payout: 40 }, { minCount: 10, maxCount: 11, payout: 100 }, { minCount: 12, maxCount: 30, payout: 300 }], displayMode: 'color' },
    { id: 'H4', name: 'Chalice', category: 'high', color: '#16A34A', textColor: '#FFFFFF', weight: 40, freeGameWeight: 100, paytable: [{ minCount: 8, maxCount: 9, payout: 30 }, { minCount: 10, maxCount: 11, payout: 40 }, { minCount: 12, maxCount: 30, payout: 240 }], displayMode: 'color' },
    { id: 'N1', name: 'Blue Gem', category: 'normal', color: '#3B82F6', textColor: '#FFFFFF', weight: 100, freeGameWeight: 100, paytable: [{ minCount: 8, maxCount: 9, payout: 20 }, { minCount: 10, maxCount: 11, payout: 30 }, { minCount: 12, maxCount: 30, payout: 200 }], displayMode: 'color' },
    { id: 'N2', name: 'Green Gem', category: 'normal', color: '#22C55E', textColor: '#FFFFFF', weight: 100, freeGameWeight: 45, paytable: [{ minCount: 8, maxCount: 9, payout: 16 }, { minCount: 10, maxCount: 11, payout: 24 }, { minCount: 12, maxCount: 30, payout: 160 }], displayMode: 'color' },
    { id: 'N3', name: 'Red Gem', category: 'normal', color: '#EF4444', textColor: '#FFFFFF', weight: 100, freeGameWeight: 40, paytable: [{ minCount: 8, maxCount: 9, payout: 10 }, { minCount: 10, maxCount: 11, payout: 20 }, { minCount: 12, maxCount: 30, payout: 100 }], displayMode: 'color' },
    { id: 'N4', name: 'Yellow Gem', category: 'normal', color: '#EAB308', textColor: '#000000', weight: 100, freeGameWeight: 40, paytable: [{ minCount: 8, maxCount: 9, payout: 8 }, { minCount: 10, maxCount: 11, payout: 18 }, { minCount: 12, maxCount: 30, payout: 80 }], displayMode: 'color' },
    { id: 'N5', name: 'Purple Gem', category: 'normal', color: '#A855F7', textColor: '#FFFFFF', weight: 100, freeGameWeight: 40, paytable: [{ minCount: 8, maxCount: 9, payout: 5 }, { minCount: 10, maxCount: 11, payout: 15 }, { minCount: 12, maxCount: 30, payout: 40 }], displayMode: 'color' },
    { id: 'SC', name: 'Scatter', category: 'scatter', color: '#F59E0B', textColor: '#000000', weight: 16, freeGameWeight: 12, paytable: [], displayMode: 'color' },
    { id: 'BN', name: 'Multiplier', category: 'bonus', color: '#EC4899', textColor: '#FFFFFF', weight: 5, freeGameWeight: 10, paytable: [], displayMode: 'color' },
  ],

  cascade: {
    enabled: true,
    removeDuration: 0.3,
    fillDuration: 0.5,
    gravityDuration: 0.25,
    columnDelay: 30,
    fallStyle: 'bounce',
    winHighlightScale: 1.1,
  },

  multiplierPool: [
    { value: 2, weight: 285000 },
    { value: 3, weight: 149012 },
    { value: 4, weight: 59605 },
    { value: 5, weight: 23842 },
    { value: 6, weight: 9537 },
    { value: 8, weight: 3815 },
    { value: 10, weight: 1526 },
    { value: 12, weight: 610 },
    { value: 15, weight: 244 },
    { value: 20, weight: 98 },
    { value: 25, weight: 39 },
    { value: 50, weight: 16 },
    { value: 100, weight: 6 },
    { value: 250, weight: 3 },
    { value: 500, weight: 1 },
  ],

  freeSpins: {
    triggerSymbolId: 'SC',
    triggerCount: 4,
    spinsAwarded: 15,
    retriggerCount: 3,
    retriggerSpinsAwarded: 5,
  },

  economy: {
    betLevels: [20, 30, 2000, 10000],
    startingBalance: 10000,
    buyFeatureEnabled: true,
    buyFeatureCostMultiplier: 100,
    maxWinMultiplier: 50000,
  },

  spinAnimation: {
    spinDuration: 0.5,
    turboMultiplier: 3,
    winPresentDuration: 0.8,
  },

  uiLayout: {},

  background: {
    color: '#18181b',
  },
};
